/**
 * useUserConfig - React Query hooks for user config management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v1UsersApi } from '../api'

/**
 * Query key for user config
 */
export const userConfigKeys = {
  all: ['v1', 'userConfig'] as const,
  user: (userId: string) => [...userConfigKeys.all, userId] as const,
}

/**
 * Hook to get user config flags
 */
export function useUserConfig(userId: string) {
  return useQuery({
    queryKey: userConfigKeys.user(userId),
    queryFn: () => v1UsersApi.getUserConfig(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to update user config flags (one or multiple)
 */
export function useUpdateUserConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      userId,
      updates,
    }: {
      userId: string
      updates: Record<string, boolean>
    }) => v1UsersApi.updateUserConfig(userId, updates),
    onSuccess: (_, variables) => {
      // Invalidate the specific user's config
      queryClient.invalidateQueries({
        queryKey: userConfigKeys.user(variables.userId),
      })
    },
  })
}
